import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";

export type Crumb = { label: string; href?: string };

/**
 * Inside a service's tabbed frame the frame carries the title and the Back link, so a page's own
 * header shrinks to a line with its description and its actions.
 */
const EmbeddedHeader = createContext(false);

export type ServiceTab = { key: string; label: string; href: string };

/** One service, one page: the service's title, its tabs (as links), and the open tab's content. */
export function ServiceFrame({ title, description, tabs, active, crumbs, back, children }: { title: string; description: ReactNode; tabs: ServiceTab[]; active: string; crumbs?: Crumb[]; back?: Crumb; children: ReactNode }) {
  return (
    <>
      <PageHeader title={title} description={description} crumbs={crumbs} back={back ?? false} />
      {tabs.length > 1 && (
        <nav aria-label={`${title} sections`} className="-mt-2 mb-5 overflow-x-auto">
          <div className="inline-flex min-h-10 items-center gap-0.5 rounded-lg border border-card-border bg-secondary p-1 text-muted-foreground">
            {tabs.map((t) => (
              <Link
                key={t.key}
                href={t.href}
                aria-current={t.key === active ? "page" : undefined}
                className={cn(
                  "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 hover:bg-card/70 hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary",
                  t.key === active && "bg-primary font-semibold text-primary-foreground shadow-sm hover:bg-primary hover:text-primary-foreground",
                )}
              >
                {t.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
      <EmbeddedHeader.Provider value>{children}</EmbeddedHeader.Provider>
    </>
  );
}

/**
 * Page heading: a Back button, breadcrumb trail, title, one line of context, and the page's main
 * actions. Back goes to the page above this one (the nearest breadcrumb that links elsewhere),
 * unless a page names its own (`back`), or has none (`back={false}`).
 */
export function PageHeader({ title, description, actions, crumbs, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; crumbs?: Crumb[]; back?: Crumb | false }) {
  const [location] = useLocation();
  const embedded = useContext(EmbeddedHeader);
  if (embedded) {
    if (!description && !actions) return null;
    return (
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 text-sm text-muted-foreground">{description}</div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    );
  }
  const parent = back === false ? null : (back ?? [...(crumbs ?? [])].reverse().find((c) => c.href && c.href.split("?")[0] !== location));
  return (
    <div className="mb-6 flex flex-col gap-3 border-b border-border pb-5 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0 space-y-1">
        {parent?.href && (
          <Link
            href={parent.href}
            className="-ml-1 mb-1.5 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to {parent.label}
          </Link>
        )}
        {crumbs && crumbs.length > 0 && (
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
              {crumbs.map((c, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  {i > 0 && <ChevronRight className="h-3.5 w-3.5 opacity-50" aria-hidden />}
                  {/* The first step (the service or area) reads as the page's section label. */}
                  {c.href ? (
                    <Link href={c.href} className={cn("text-primary hover:underline", i === 0 && "text-xs font-semibold uppercase tracking-[0.1em]")}>
                      {c.label}
                    </Link>
                  ) : (
                    <span className={i === 0 ? "text-xs font-semibold uppercase tracking-[0.1em] text-primary" : undefined}>{c.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
        <h1 className="text-xl font-semibold tracking-tight text-foreground md:text-[1.375rem]">{title}</h1>
        {description && <div className="text-sm text-muted-foreground">{description}</div>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Section({ id, title, action, children, className, description }: { id?: string; title: string; action?: ReactNode; children: ReactNode; className?: string; description?: ReactNode }) {
  return (
    <section id={id} className={cn("overflow-hidden rounded-lg border border-card-border bg-card", className)}>
      <div className="flex flex-col items-start gap-2 border-b border-border bg-secondary/50 px-5 py-3 sm:flex-row sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">{children}</p>;
}

/**
 * Holds the place of what is loading at about the height of what replaces it, so what loads in does
 * not push the content below it down. The default suits a sentence or an empty list; `size="box"` a
 * short list or form (16rem), `size="list"` a page of a list (30rem); `tall` a whole page or a list
 * longer than the screen.
 */
export function Loading({ tall, size }: { tall?: boolean; size?: "box" | "list" } = {}) {
  return <div className={cn("py-10 text-center text-sm text-muted-foreground", size === "box" && "min-h-64", size === "list" && "min-h-[30rem]", tall && "min-h-[80vh]")}>Loading…</div>;
}

/**
 * A grey placeholder shaped like what is loading. Pass the height of what replaces it (a `min-h-*`
 * class, with `sm:`/`lg:` steps where the loaded content is taller on a phone), so the content
 * below it does not move when the real thing arrives. Screen readers hear `label`.
 */
export function LoadingBox({ className, label = "Loading…" }: { className: string; label?: string }) {
  return (
    <div role="status" aria-busy="true" className={cn("rounded-lg border border-border bg-muted/40 motion-safe:animate-pulse", className)}>
      <span className="sr-only">{label}</span>
    </div>
  );
}

type Stat = { label: string; value: ReactNode; tone?: "default" | "warning" | "danger" | "success"; selected?: boolean; onClick?: () => void };

/**
 * A row of headline figures above a list: label, value, and an optional tone for what needs
 * attention. With `onClick` each figure is a toggle that shows its list; the selected one is
 * filled with its color.
 */
export function StatRow({ stats }: { stats: Stat[] }) {
  const tones = {
    default: { box: "border-card-border bg-card", value: "text-foreground", dot: "bg-primary/60", on: "border-primary bg-primary text-primary-foreground" },
    warning: { box: "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40", value: "text-amber-700 dark:text-amber-300", dot: "bg-amber-500", on: "border-amber-500 bg-amber-500 text-white dark:bg-amber-600" },
    danger: { box: "border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40", value: "text-red-700 dark:text-red-300", dot: "bg-red-500", on: "border-red-600 bg-red-600 text-white" },
    success: { box: "border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40", value: "text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500", on: "border-emerald-600 bg-emerald-600 text-white" },
  } as const;
  return (
    <ul className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
      {stats.map((s) => {
        const t = tones[s.tone ?? "default"];
        const body = (
          <>
            <span className={cn("flex items-start gap-2 text-xs font-medium leading-4", s.selected ? "text-current opacity-90" : "text-muted-foreground")}>
              <span className={cn("mt-1 h-2 w-2 shrink-0 rounded-full", s.selected ? "bg-current" : t.dot)} aria-hidden />
              <span className="line-clamp-2">{s.label}</span>
            </span>
            <span className={cn("tabular mt-1 block text-2xl font-semibold", s.selected ? "text-current" : t.value)}>{s.value}</span>
          </>
        );
        const box = cn("min-w-0 rounded-lg border px-4 py-3 text-left shadow-[var(--card-shadow)]", s.selected ? t.on : t.box);
        // A list of tiles (not a definition list): a tile can be a button, which a <dl> may not hold.
        return (
          <li key={s.label} className="min-w-0">
            {s.onClick ? (
              <button
                type="button"
                aria-pressed={!!s.selected}
                onClick={s.onClick}
                className={cn(box, "block h-full w-full transition duration-150 hover:-translate-y-px hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary")}
              >
                {body}
              </button>
            ) : (
              <div className={cn(box, "h-full")}>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Switches between views of one page (filters and sub-lists): the chosen one is filled with the
 * brand color, the others are quiet; each may carry a count.
 */
export function Segmented<T extends string>({ options, value, onChange, label, className }: { options: ReadonlyArray<{ value: T; label: ReactNode; count?: number }>; value: T; onChange: (v: T) => void; label: string; className?: string }) {
  return (
    // One row that scrolls sideways on phones; wraps on wider screens.
    <div role="tablist" aria-label={label} className={cn("inline-flex max-w-full gap-1 overflow-x-auto rounded-lg border border-card-border bg-secondary/70 p-1 [scrollbar-width:none] sm:flex-wrap", className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
              on ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-card hover:text-foreground",
            )}
          >
            {o.label}
            {o.count !== undefined && <span className={cn("tabular rounded-full px-1.5 text-[11px] leading-[18px]", on ? "bg-black/25 text-current" : "border border-border bg-card text-muted-foreground")}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Brand({ light }: { light?: boolean }) {
  return (
    <span className="flex items-center gap-2.5">
      <img src="/logo.png" alt="" className="h-7 w-7 rounded" />
      {/* One name for the wordmark ("ClientFlow"), not two coloured halves; the visible letters are hidden from screen readers. */}
      <span role="img" aria-label="ClientFlow" className={cn("font-display text-lg font-semibold tracking-wide", light ? "text-sidebar-foreground" : "text-foreground")}>
        <span aria-hidden="true">Client</span>
        <span aria-hidden="true" className={light ? "text-accent" : "text-[#876614] dark:text-accent"}>
          Flow
        </span>
      </span>
    </span>
  );
}

// --- Long lists ---------------------------------------------------------------------------------

export const PAGE_SIZES = [10, 25, 50, 100] as const;

/**
 * One page of a long list: 10 rows by default, with 25, 50 or 100 to choose. Goes back to the first
 * page when the list itself changes (a new filter or search), and never past the last page.
 */
export function usePaged<T>(items: readonly T[], opts: { size?: number; resetKey?: unknown } = {}) {
  const [size, setSizeState] = useState<number>(opts.size ?? PAGE_SIZES[0]);
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(items.length / size));
  useEffect(() => setPage(0), [opts.resetKey, items.length]);
  const current = Math.min(page, pages - 1);
  return {
    items: items.slice(current * size, current * size + size),
    page: current,
    pages,
    size,
    total: items.length,
    setPage,
    setSize: (n: number) => {
      setSizeState(n);
      setPage(0);
    },
  };
}
export type Paged = Pick<ReturnType<typeof usePaged>, "page" | "pages" | "size" | "total" | "setPage" | "setSize">;

/** "1–10 of 500", rows per page, and Previous / Next. Hidden while everything fits on one page. */
export function Pager({ paged, noun = "items", className, compact }: { paged: Paged; noun?: string; className?: string; compact?: boolean }) {
  const { page, pages, size, total, setPage, setSize } = paged;
  if (total <= PAGE_SIZES[0]) return null;
  const from = page * size + 1;
  const to = Math.min(total, from + size - 1);
  const nav = "inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2.5 text-sm font-medium transition hover:bg-muted disabled:pointer-events-none disabled:opacity-40";
  return (
    <nav aria-label={`Pages of ${noun}`} className={cn("flex gap-2 border-t border-border pt-3 text-sm", compact ? "items-center justify-between" : "flex-col sm:flex-row sm:items-center sm:justify-between", className)}>
      <p className="tabular text-muted-foreground" aria-live="polite">
        {from.toLocaleString("en-US")}–{to.toLocaleString("en-US")} of {total.toLocaleString("en-US")} {noun}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className={cn("items-center gap-1.5 text-muted-foreground", compact ? "hidden" : "flex")}>
          <span className="whitespace-nowrap">Show</span>
          <select className="h-8 rounded-md border border-input bg-background px-1.5 text-sm text-foreground" value={size} onChange={(e) => setSize(Number(e.target.value))}>
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className={nav} onClick={() => setPage(page - 1)} disabled={page === 0} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" aria-hidden /> {!compact && "Previous"}
        </button>
        {!compact && (
          <span className="tabular whitespace-nowrap px-1 text-muted-foreground">
            Page {page + 1} of {pages}
          </span>
        )}
        <button type="button" className={nav} onClick={() => setPage(page + 1)} disabled={page >= pages - 1} aria-label="Next page">
          {!compact && "Next"} <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </nav>
  );
}

/** A search box for a list. */
export function ListSearch({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder: string; className?: string }) {
  return (
    <label className={cn("relative block w-full sm:w-72", className)}>
      <span className="sr-only">{placeholder}</span>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
    </label>
  );
}

/** Case- and accent-insensitive match of a search against some text. */
export function matches(search: string, ...texts: Array<string | null | undefined>): boolean {
  const norm = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const q = norm(search.trim());
  return !q || texts.some((t) => t && norm(t).includes(q));
}

/** The first few of a short list, with "Show all" for the rest. */
export function ShowMore<T>({ items, initial = 5, render, noun = "more" }: { items: readonly T[]; initial?: number; render: (item: T) => ReactNode; noun?: string }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, initial);
  return (
    <>
      {shown.map(render)}
      {items.length > initial && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" aria-expanded={all}>
          {all ? "Show fewer" : `Show all ${items.length} ${noun}`}
          <ChevronDown className={cn("h-4 w-4 transition-transform", all && "rotate-180")} aria-hidden />
        </button>
      )}
    </>
  );
}

/**
 * Page state for a list the server sends a page at a time: pass `offset` and `size` to the API
 * (`?offset=&limit=`), then `paged(total)` to <Pager>. Goes back to the first page when `resetKey`
 * changes (a new filter or search).
 */
export function useServerPaged(resetKey?: unknown, initialSize: number = PAGE_SIZES[0]) {
  const [size, setSizeState] = useState(initialSize);
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [resetKey]);
  return {
    offset: page * size,
    size,
    paged: (total: number): Paged => {
      const pages = Math.max(1, Math.ceil(total / size));
      return {
        page: Math.min(page, pages - 1),
        pages,
        size,
        total,
        setPage,
        setSize: (n: number) => {
          setSizeState(n);
          setPage(0);
        },
      };
    },
  };
}
